/**
 * OpenRolesSection — Public unfilled role slots on the Impact tab.
 *
 * Shows roles where visibility = 'public' and status != 'draft'/'closed'
 * with available spots remaining.
 *
 * Displayed inside a panel container that matches the "What's Needed" panel:
 *   - Header bar with title + count badge
 *   - Progress bar showing roles filled / total
 *   - Card list with show more / show less toggle
 *
 * Each card is click-to-expand with inline detail (full description,
 * skills tags, time commitment, Apply button).
 *
 * State machine per slot:
 *   Default        → [Apply] button in expanded detail
 *   After apply    → card collapses, shows "Applied ✓" chip + [Withdraw]
 *   After withdraw → [Apply] button (clean reset)
 *
 * Users can apply for multiple roles simultaneously.
 */

import React, { useMemo, useState, useCallback } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { useApiProjectRoles } from "../../api/useApiProjectRoles";
import { assignUserToRole, updateRoleAssignment } from "../../api/projectRoles";
import type { ProjectRoleSlotResponse } from "../../api/projectRoles";
import styles from "./OpenRolesSection.module.css";

const PREVIEW_COUNT = 3;

interface Props {
  projectGuid: string;
  currentUserGuid: string;
}

type SlotState = "none" | "applied" | "applying" | "withdrawing" | "withdrawn";

const OpenRolesSection: React.FC<Props> = ({ projectGuid, currentUserGuid }) => {
  const { t } = useAppTranslation("projectTeam");
  const { slots, loading, refetch } = useApiProjectRoles(projectGuid);

  const [slotStates, setSlotStates] = useState<Map<string, SlotState>>(new Map());
  const [assignmentMap, setAssignmentMap] = useState<Map<string, string>>(new Map());
  const [expanded, setExpanded] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  /* ── Public open roles ── */
  const openRoles = useMemo(
    () =>
      slots.filter(
        (s) =>
          s.visibility === "public" &&
          s.status !== "draft" &&
          s.status !== "closed" &&
          s.slotsFilled < s.slotsNeeded
      ),
    [slots]
  );

  /* ── All public roles (for progress bar) ── */
  const allPublicRoles = useMemo(
    () => slots.filter((s) => s.visibility === "public" && s.status !== "draft"),
    [slots]
  );

  const totalSlots = useMemo(
    () => allPublicRoles.reduce((sum, s) => sum + s.slotsNeeded, 0),
    [allPublicRoles]
  );
  const filledSlots = useMemo(
    () => allPublicRoles.reduce((sum, s) => sum + s.slotsFilled, 0),
    [allPublicRoles]
  );
  const filledPct = totalSlots > 0 ? Math.round((filledSlots / totalSlots) * 100) : 0;

  /* ── Show more / less ── */
  const hasMore = openRoles.length > PREVIEW_COUNT;
  const visibleRoles = expanded ? openRoles : openRoles.slice(0, PREVIEW_COUNT);
  const toggleExpanded = useCallback(() => setExpanded((v) => !v), []);

  /* ── Card expand / collapse ── */
  const toggleCard = useCallback((guid: string) => {
    setExpandedCard((prev) => (prev === guid ? null : guid));
  }, []);

  /* ── Slot state derivation ── */
  const effectiveState = useCallback(
    (slot: ProjectRoleSlotResponse): SlotState => {
      const key = slot.roleSlotGuid.toUpperCase();
      const override = slotStates.get(key);
      if (override !== undefined) return override;
      const match = slot.assignments?.find(
        (a) =>
          a.userGuid.toUpperCase() === currentUserGuid.toUpperCase() &&
          (a.status === "active" || a.status === "pending_approval")
      );
      if (match) return "applied";
      return "none";
    },
    [slotStates, currentUserGuid]
  );

  const findAssignmentGuid = useCallback(
    (slot: ProjectRoleSlotResponse): string | null => {
      const key = slot.roleSlotGuid.toUpperCase();
      const cached = assignmentMap.get(key);
      if (cached) return cached;
      const match = slot.assignments?.find(
        (a) =>
          a.userGuid.toUpperCase() === currentUserGuid.toUpperCase() &&
          (a.status === "active" || a.status === "pending_approval")
      );
      return match?.assignmentGuid ?? null;
    },
    [assignmentMap, currentUserGuid]
  );

  /* ── Apply → collapse card + show Applied chip ── */
  const handleApply = useCallback(
    async (slot: ProjectRoleSlotResponse) => {
      const key = slot.roleSlotGuid.toUpperCase();
      setSlotStates((prev) => new Map(prev).set(key, "applying"));
      try {
        const result = await assignUserToRole(projectGuid, slot.roleSlotGuid, {
          userGuid: currentUserGuid,
          assignedBy: null,
        });
        setSlotStates((prev) => new Map(prev).set(key, "applied"));
        setExpandedCard(null); // collapse on apply
        if (result?.assignmentGuid) {
          setAssignmentMap((prev) => new Map(prev).set(key, result.assignmentGuid));
        }
        await refetch();
      } catch (err) {
        console.error("[OpenRoles] Apply failed:", err);
        setSlotStates((prev) => new Map(prev).set(key, "none"));
      }
    },
    [projectGuid, currentUserGuid, refetch]
  );

  const handleWithdraw = useCallback(
    async (slot: ProjectRoleSlotResponse) => {
      const key = slot.roleSlotGuid.toUpperCase();
      const assignGuid = findAssignmentGuid(slot);
      if (!assignGuid) {
        console.error("[OpenRoles] Cannot withdraw — no assignment GUID found");
        return;
      }
      setSlotStates((prev) => new Map(prev).set(key, "withdrawing"));
      try {
        await updateRoleAssignment(projectGuid, slot.roleSlotGuid, assignGuid, {
          status: "withdrawn",
        });
        setSlotStates((prev) => new Map(prev).set(key, "withdrawn"));
        setAssignmentMap((prev) => {
          const next = new Map(prev);
          next.delete(key);
          return next;
        });
        await refetch();
      } catch (err) {
        console.error("[OpenRoles] Withdraw failed:", err);
        setSlotStates((prev) => new Map(prev).set(key, "applied"));
      }
    },
    [projectGuid, findAssignmentGuid, refetch]
  );

  if (loading) return null;

  return (
    <div className={styles.panel}>
      {/* ── Header bar ── */}
      <div className={styles.panelHeader}>
        <h3 className={styles.panelTitle}>{t("openRoles.title")}</h3>
        {openRoles.length > 0 && (
          <span className={styles.panelBadge}>
            {openRoles.length} {t("openRoles.available")}
          </span>
        )}
      </div>

      {/* ── Progress bar ── */}
      {totalSlots > 0 && (
        <div className={styles.progressRow}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${filledPct}%` }}
            />
          </div>
          <span className={styles.progressLabel}>
            {t("openRoles.rolesFilled", { filled: filledSlots, total: totalSlots })}
          </span>
        </div>
      )}

      {/* ── Card list ── */}
      <div className={styles.panelBody}>
        {openRoles.length === 0 ? (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>{t("openRoles.noRoles")}</p>
            <p className={styles.emptyHint}>{t("openRoles.noRolesHint")}</p>
          </div>
        ) : (
          <div className={styles.list}>
            {visibleRoles.map((slot) => {
              const state = effectiveState(slot);
              const isOpen = expandedCard === slot.roleSlotGuid;
              return (
                <OpenRoleCard
                  key={slot.roleSlotGuid}
                  slot={slot}
                  t={t}
                  state={state}
                  isOpen={isOpen}
                  onToggle={toggleCard}
                  onApply={handleApply}
                  onWithdraw={handleWithdraw}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* ── Show more / less toggle ── */}
      {hasMore && (
        <button type="button" className={styles.showToggle} onClick={toggleExpanded}>
          {expanded
            ? t("openRoles.showLess")
            : t("openRoles.showAll", { count: openRoles.length })}
        </button>
      )}
    </div>
  );
};

/* ── Role category → icon emoji ── */

const ROLE_CATEGORY_ICONS: Record<string, string> = {
  leadership: '\u{1F451}',
  technical: '\u{1F4BB}',
  creative: '\u{1F3A8}',
  operations: '\u{2699}️',
  communications: '\u{1F4E3}',
  research: '\u{1F52C}',
  education: '\u{1F4DA}',
  finance: '\u{1F4B0}',
  community: '\u{1F91D}',
  field_work: '\u{1F477}',
  volunteer: '\u{2B50}',
  advisory: '\u{1F4AC}',
};

/* ── Duration type → label key ── */

const DURATION_KEYS: Record<string, string> = {
  project_duration: "openRoles.durationProject",
  phase_duration: "openRoles.durationPhase",
  fixed_term: "openRoles.durationFixed",
  ongoing: "openRoles.durationOngoing",
};

function OpenRoleCard({
  slot,
  t,
  state,
  isOpen,
  onToggle,
  onApply,
  onWithdraw,
}: {
  slot: ProjectRoleSlotResponse;
  t: (key: string, opts?: Record<string, unknown>) => string;
  state: SlotState;
  isOpen: boolean;
  onToggle: (guid: string) => void;
  onApply: (slot: ProjectRoleSlotResponse) => void;
  onWithdraw: (slot: ProjectRoleSlotResponse) => void;
}) {
  const remaining = slot.slotsNeeded - slot.slotsFilled;
  const isBusy = state === "applying" || state === "withdrawing";
  const icon = ROLE_CATEGORY_ICONS[slot.roleCategory] || '\u{1F464}';
  const durationKey = DURATION_KEYS[slot.durationType] || null;

  return (
    <div
      className={`${styles.roleCard} ${isOpen ? styles.roleCardOpen : ''}`}
      onClick={() => onToggle(slot.roleSlotGuid)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(slot.roleSlotGuid); } }}
    >
      {/* ── Top row: icon + name + meta badges ── */}
      <div className={styles.categoryIcon}>{icon}</div>

      <div className={styles.roleInfo}>
        <span className={styles.roleName}>{slot.roleLabel}</span>
        {!isOpen && slot.description && (
          <span className={styles.roleDescription}>{slot.description}</span>
        )}
        <div className={styles.roleMeta}>
          {slot.timeCommitment && (
            <span className={styles.timeBadge}>{slot.timeCommitment}</span>
          )}
          {slot.phase && (
            <>
              {slot.timeCommitment && <span className={styles.metaSep}>{'·'}</span>}
              <span className={styles.phaseBadge}>{slot.phase}</span>
            </>
          )}
          {durationKey && (
            <>
              {(slot.timeCommitment || slot.phase) && <span className={styles.metaSep}>{'·'}</span>}
              <span className={styles.timeBadge}>{t(durationKey)}</span>
            </>
          )}
          {remaining > 1 && (
            <>
              {(slot.timeCommitment || slot.phase || durationKey) && <span className={styles.metaSep}>{'·'}</span>}
              <span className={styles.spotsBadge}>
                {t("openRoles.spotsLeft", { count: remaining })}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Right column: status chip (compact only) */}
      <div className={styles.slotActions}>
        {state === "applied" && (
          <span className={styles.appliedChip}>
            {t("openRoles.applied")} {'✓'}
          </span>
        )}
        {state === "withdrawn" && (
          <span className={styles.withdrawnChip}>
            {t("openRoles.withdrawn")}
          </span>
        )}
      </div>

      {/* ── Expanded detail (unfolds below the top row, same card) ── */}
      {isOpen && (
        <div className={styles.cardDetail}>
          {slot.description && (
            <p className={styles.detailText}>{slot.description}</p>
          )}

          {slot.skillsRequired && slot.skillsRequired.length > 0 && (
            <div className={styles.skillsList}>
              {slot.skillsRequired.map((skill) => (
                <span key={skill} className={styles.skillTag}>{skill}</span>
              ))}
            </div>
          )}

          <div className={styles.detailFooter}>
            <span className={styles.detailCommitment}>
              {remaining === 1
                ? t("openRoles.spotOpen", { count: 1 })
                : t("openRoles.spotsOpen", { count: remaining })}
            </span>

            {state === "applied" || state === "withdrawing" ? (
              <button
                type="button"
                className={styles.withdrawBtn}
                onClick={(e) => { e.stopPropagation(); onWithdraw(slot); }}
                disabled={isBusy}
              >
                {state === "withdrawing"
                  ? t("openRoles.withdrawing")
                  : t("openRoles.withdraw")}
              </button>
            ) : (
              <button
                type="button"
                className={styles.detailApplyBtn}
                onClick={(e) => { e.stopPropagation(); onApply(slot); }}
                disabled={isBusy}
              >
                {state === "applying"
                  ? t("openRoles.applying")
                  : t("openRoles.applyForRole")}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OpenRolesSection;
